import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, MessageCircle, Mail, Phone, Plus, HelpCircle } from "lucide-react";
import { api, fmtDate, apiError } from "@/lib/api";
import { Skeleton, StatusBadge, EmptyState } from "@/components/common";
import { useT } from "@/context/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose,
} from "@/components/ui/dialog";

const cats = ["Payment", "Loan", "Rewards", "Account", "Other"];
const inputCls = "w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]";

export default function Support() {
  const [tickets, setTickets] = useState(null);
  const t = useT();
  const [loans, setLoans] = useState([]);
  const [contact, setContact] = useState(null);
  const [f, setF] = useState({ category: "Payment", subject: "", description: "", loan_id: "" });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => {
    load();
    api.get("/loans").then((r) => setLoans(r.data)).catch(() => {});
    api.get("/support-contact").then((r) => setContact(r.data || {})).catch(() => setContact({}));
  }, []);
  const submit = async () => {
    if (!f.subject || !f.description) { toast.error("Please add a subject and description"); return; }
    try { await api.post("/support/tickets", { ...f, loan_id: f.loan_id || null }); toast.success("Ticket raised"); setOpen(false); setF({ category: "Payment", subject: "", description: "", loan_id: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const waDigits = (contact?.whatsapp || "").replace(/[^0-9]/g, "");
  const channels = [
    contact?.whatsapp && { icon: MessageCircle, label: "WhatsApp", desc: "Chat with us", action: () => window.open(`https://wa.me/${waDigits}`, "_blank") },
    contact?.phone && { icon: Phone, label: "Call Us", desc: contact.phone, action: () => { window.location.href = `tel:${contact.phone}`; } },
    contact?.email && { icon: Mail, label: "Email", desc: contact.email, action: () => { window.location.href = `mailto:${contact.email}`; } },
  ].filter(Boolean);
  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">{t("Support Center")}</h1><p className="text-sm text-slate-500">{t("We're here to help. Reach us or raise a ticket.")}</p></div>

      {channels.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          {channels.map((c) => (
            <button key={c.label} onClick={c.action} data-testid={`support-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-[#14B8A6]/50 hover:shadow-md">
              <span className="grid h-10 w-10 place-items-center rounded-md bg-emerald-50 text-[#14B8A6]"><c.icon size={20} /></span>
              <span><span className="block font-semibold text-[#0B132B]">{t(c.label)}</span><span className="block text-xs text-slate-500">{t(c.desc)}</span></span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-[#0B132B]">{t("Your Tickets")}</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button data-testid="raise-ticket" className="inline-flex items-center gap-1.5 rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0D9488]"><Plus size={16} /> {t("Raise Ticket")}</button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Raise a support ticket</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><label className="text-sm font-medium text-slate-700">Category</label><select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ticket-category">{cats.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div><label className="text-sm font-medium text-slate-700">Related loan (optional)</label><select className={inputCls} value={f.loan_id} onChange={(e) => setF({ ...f, loan_id: e.target.value })} data-testid="ticket-loan"><option value="">None</option>{loans.map((l) => <option key={l.id} value={l.id}>{l.lender} — {l.loan_type}</option>)}</select></div>
              <div><label className="text-sm font-medium text-slate-700">Subject *</label><input className={inputCls} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Brief summary" data-testid="ticket-subject" /></div>
              <div><label className="text-sm font-medium text-slate-700">Description *</label><textarea rows={4} className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Describe your issue" data-testid="ticket-desc" /></div>
            </div>
            <DialogFooter><DialogClose asChild><button className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold">Cancel</button></DialogClose><button onClick={submit} data-testid="ticket-submit" className="rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white">Submit</button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!tickets ? <Skeleton className="h-32" /> : tickets.length === 0 ? <EmptyState icon={LifeBuoy} title={t("No tickets yet")} desc={t("Raise a ticket and our team will get back to you.")} /> : (
        <div className="space-y-3">
          {tickets.map((tk) => (
            <div key={tk.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between"><div><p className="font-semibold text-[#0B132B]">{tk.subject}</p><p className="text-xs text-slate-400">{tk.ticket_no} • {tk.category} • {fmtDate(tk.created_at)}</p></div><StatusBadge status={tk.status} /></div>
              <p className="mt-2 text-sm text-slate-500">{tk.description}</p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><HelpCircle size={16} /> {t("Quick FAQ")}</h3>
        <div className="mt-3 space-y-3 text-sm">
          {[["How do I pay my EMI?", "Open a loan and tap Pay EMI, then choose your payment method."], ["When are points credited?", "Points are credited server-side after a successful on-time EMI payment."]].map(([q, a]) => (
            <div key={q}><p className="font-medium text-[#0B132B]">{q}</p><p className="text-slate-500">{a}</p></div>
          ))}
        </div>
      </div>
    </div>
  );
}
